import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { CircularProgress, IconButton, Popover, Tooltip, Typography } from '@mui/material'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { AppPaper } from 'src/component/common/analog/molecule/AppPaper'
import { AppButton } from 'src/component/common/analog/molecule/button/AppButton'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'
import { useProjectBuilderStore } from '../../store/project-builder.store'
import { useNotificationStore } from 'src/feature/notification/store/notification.store'
import { EditProjectDates } from './EditProjectDates'
import { RemoveProjectUser } from './RemoveProjectUser'
import { MatchedUsersList } from './MatchedUsersList'
import { AddUsersModal } from './AddUsersModal'
import { ColorTokens, Palette } from 'src/feature/theme/model/Palette'
import { AppAvatar } from 'src/component/common/analog/molecule/AppAvatar'
import { AppCheckbox } from 'src/feature/form/component/analog/AppCheckbox'
import { AppFormControlWithLabel } from 'src/feature/form/component/analog/AppFormControlWithLabel'
import { API_BASE_URL } from 'src/util/constant'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import GroupsIcon from '@mui/icons-material/Groups'
import { ProjectNotificationPrompt } from './ProjectNotificationPrompt'

type ProjectDetailsCardProps = {
  projectId: string
}

export const ProjectDetailsCard = observer(({
  projectId,
}: ProjectDetailsCardProps) => {
  const { translation, locale } = useLocalizationStore()
  const projectBuilderTranslation = translation.pages.projectBuilder
  const projectBuilderStore = useProjectBuilderStore()
  const notificationStore = useNotificationStore()
  // For new, recommended users
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set())
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const [showNotificationPrompt, setShowNotificationPrompt] = useState(false)
  // For removing users from project
  const [selectedProjectUsers, setSelectedProjectUsers] = useState<Set<string>>(new Set())

  // Fetch project details
  useEffect(() => {
    if (!projectId) {
      return
    }

    const load = async () => {
      await projectBuilderStore.getProjectDetails(projectId)

      await projectBuilderStore.getRecommendedUsers(projectId)
    }

    load()
  }, [
    projectId,
    projectBuilderStore,
  ])

  // Store data
  const project = projectBuilderStore.projectDetails

  const usersWithUsedSkills = projectBuilderStore.recommendedUsers?.usersWithUsedSkills ?? []

  const usersWithUnusedSkills = projectBuilderStore.recommendedUsers?.usersWithUnusedSkills ?? []

  // Hide component if no project
  if (!projectId || !project) {
    return null
  }

  // Recommended users
  const allRecommendedUsers = [
    ...usersWithUsedSkills,
    ...usersWithUnusedSkills,
  ]

  // To check if user is already in project
  const uniqueRecommendedUsers = Array.from(
    new Map(
      allRecommendedUsers.map(user => [user.userId, user]),
    ).values(),
  )

  const selectedUsersList = uniqueRecommendedUsers.filter(user => selectedUsers.has(user.userId))

  // User roles format
  const formatRoles = (roles: string[]) => {
    if (!roles.length) {
      return ''
    }

    if (roles.length <= 2) {
      return roles.join(', ')
    }

    return `${roles.slice(0, 2).join(', ')} +${roles.length - 2}`
  }

  // Date format
  const formatDate = (dateString: string) => {
    const formattedLocale = locale === 'sv-SE'
      ? 'sv-SE'
      : 'en-US'

    return new Intl.DateTimeFormat(formattedLocale, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(new Date(dateString))
  }

  // Select user
  const selectUser = (userId: string) => {
    setSelectedUsers((prev) => {
      const newSet = new Set(prev)

      if (newSet.has(userId)) {
        newSet.delete(userId)
      } else {
        newSet.add(userId)
      }

      return newSet
    })
  }

  // Toggle checkbox for users in project
  const toggleProjectUser = (userId: string) => {
    setSelectedProjectUsers((prev) => {
      const newSet = new Set(prev)

      if (newSet.has(userId)) {
        newSet.delete(userId)
      } else {
        newSet.add(userId)
      }

      return newSet
    })
  }

  // Add User
  const handleAddUsers = () => {
    setIsAddModalOpen(false)
    setShowNotificationPrompt(true)
  }

  // Confirm adding users and choose whether to notify them
  const handleConfirmAddUsers = async (notifyUsers: boolean) => {
    try {
      await projectBuilderStore.addUsersToProject(
        projectId,
        Array.from(selectedUsers),
        notifyUsers,
      )
      await projectBuilderStore.getProjects()

      notificationStore.enqueueSuccess(
        projectBuilderTranslation.addUsersSuccess(),
      )

      setSelectedUsers(new Set())
      setShowNotificationPrompt(false)
      setIsAddModalOpen(false)
    } catch (error) {
      notificationStore.enqueueError(
        error instanceof Error
          ? error.message
          : projectBuilderTranslation.addUsersFailed(),
      )
    }
  }

  // Remove user
  const handleRemoveUser = async (userId: string) => {
    try {
      await projectBuilderStore.removeProjectUser(
        projectId,
        userId,
      )
      await projectBuilderStore.getProjects()

      await projectBuilderStore.getRecommendedUsers(projectId)

      notificationStore.enqueueSuccess(
        projectBuilderTranslation.removeUserSuccess(),
      )
      setSelectedProjectUsers((prev) => {
        const newSet = new Set(prev)
        newSet.delete(userId)
 
        return newSet
      })
    } catch (error) {
      notificationStore.enqueueError(
        error instanceof Error
          ? error.message
          : projectBuilderTranslation.removeUserFailed(),
      )
    }
  }

  // Save edited dates
  const handleSaveDates = async (
    startDate: string,
    endDate: string,
  ) => {
    try {
      await projectBuilderStore.updateProjectDates(
        projectId,
        startDate,
        endDate,
      )
      await projectBuilderStore.getProjects()

      notificationStore.enqueueSuccess(
        projectBuilderTranslation.updateDatesSuccess(),
      )
    } catch (error) {
      notificationStore.enqueueError(
        error instanceof Error
          ? error.message
          : projectBuilderTranslation.updateDatesFailed(),
      )
    }
  }
  const renderProjectUser = (user: any) => {
    const allSkillNames = user.matchedSkills
      ?.map((skill: any) => skill.organizationSkillName)
      .join(', ') ?? ''
 
    const isChecked = selectedProjectUsers.has(user.userId)
 
    return (
      <AppPaper key={user.userId} sx={styles.userCard}>
 
        <AppBox sx={{ display: 'flex', alignItems: 'center' }}>
          <AppFormControlWithLabel
            control={
              <AppCheckbox
                size="small"
                checked={isChecked}
                onClick={() => toggleProjectUser(user.userId)}
              />
            }
            label=""
          />
          <AppBox sx={styles.avatarWrapper}>
            <AppAvatar
              src={`${API_BASE_URL}${user.avatarUrl}`}
              outerProps={{ squareSize: 48 }}
            />
          </AppBox>
        </AppBox>
 
        <AppBox sx={styles.userInfo}>
          <Typography sx={styles.userName}>
            {user.userName}
          </Typography>
 
          {Boolean(user.roles?.length) && (
            <Tooltip title={user.roles.join(', ')} arrow placement="top">
              <Typography sx={styles.userRoles}>
                {formatRoles(user.roles)}
              </Typography>
            </Tooltip>
          )}
 
          {Boolean(allSkillNames) && (
            <Typography sx={styles.userSkill}>
              {allSkillNames}
            </Typography>
          )}
        </AppBox>

        {isChecked && (
          <RemoveProjectUser
            userName={user.userName}
            onRemove={() => handleRemoveUser(user.userId)}
            disabled={project.users.length <= 1}
          />
        )}
 
      </AppPaper>
    )
  }

  return (
    <AppBox sx={styles.container}>
      <AppPaper sx={styles.projectCard}>
        <AppBox sx={styles.cardHeader}>
          <AppBox sx={styles.headerRight}>
            <AppBox sx={styles.dateChip}>
              <Typography sx={styles.dateChipLabel}>
                {projectBuilderTranslation.startDate()}:
              </Typography>
              <Typography sx={styles.dateChipValue}>
                {formatDate(project.startDate)}
              </Typography>
            </AppBox>
 
            <AppBox sx={styles.dateChip}>
              <Typography sx={styles.dateChipLabel}>
                {projectBuilderTranslation.endDate()}
              </Typography>
              <Typography sx={styles.dateChipValue}>
                {formatDate(project.endDate)}
              </Typography>
            </AppBox>
 
            <IconButton
              size="small"
              onClick={e => setMenuAnchor(e.currentTarget)}
              sx={styles.menuButton}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
 
          </AppBox>
        </AppBox>
        <Typography variant='h2' sx={styles.projectName}>
          {project.name}
        </Typography>
 
        <Popover
          open={Boolean(menuAnchor)}
          anchorEl={menuAnchor}
          onClose={() => setMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{ sx: styles.popoverPaper }}
        >
          <AppBox sx={styles.popoverContent}>
            <AppBox sx={styles.popoverDateRow}>
              <Typography sx={styles.popoverDateLabel}>
                {projectBuilderTranslation.startDate()}:{' '}
                <span style={{ fontWeight: 600 }}>{formatDate(project.startDate)}</span>
              </Typography>
              <EditProjectDates
                field="start"
                startDate={project.startDate}
                endDate={project.endDate}
                onSave={handleSaveDates}
              />
            </AppBox>
 
            <AppBox sx={styles.popoverDateRow}>
              <Typography sx={styles.popoverDateLabel}>
                {projectBuilderTranslation.endDate()}:{' '}
                <span style={{ fontWeight: 600 }}>{formatDate(project.endDate)}</span>
              </Typography>
              <EditProjectDates
                field="end"
                startDate={project.startDate}
                endDate={project.endDate}
                onSave={handleSaveDates}
              />
            </AppBox>
          </AppBox>
        </Popover>

        <Typography variant='body1' sx={styles.projectDescription}>
          {project.description}
        </Typography>

        <AppBox sx={styles.usersSection}>
          <AppBox sx={styles.usersSectionHeader}>
            <GroupsIcon sx={styles.sectionIcon} />
            <Typography sx={styles.usersSectionTitle}>
              {projectBuilderTranslation.projectUsers()}
            </Typography>
          </AppBox>
 
          <AppBox sx={styles.usersList}>
            {project.users.map(renderProjectUser)}
          </AppBox>
        </AppBox>
      </AppPaper>

      <AppPaper sx={styles.recommendedCard}>
        <Typography sx={styles.usersSectionTitle}>
          {projectBuilderTranslation.suggestionEmployees()}
        </Typography>

        {usersWithUsedSkills.length === 0 && usersWithUnusedSkills.length === 0 ? (
          <Typography sx={styles.emptyText}>
            {projectBuilderTranslation.noRecommendedUsers()}
          </Typography>
        ) : (
          <>
            <MatchedUsersList
              usersWithUsedSkills={usersWithUsedSkills}
              usersWithUnusedSkills={usersWithUnusedSkills}
              projectUsers={project.users}
              selectedUsers={selectedUsers}
              onSelectUser={selectUser}
            />
            <AppButton
              fontSize={14}
              variant='contained'
              sx={{ alignSelf: 'center' }}
              disabled={selectedUsers.size === 0}
              onClick={() => setIsAddModalOpen(true)}
              endIcon={ isAddModalOpen ? <CircularProgress size={16} color='inherit' sx={{ margin: '0px 3px' }} /> : null }
            >
              {projectBuilderTranslation.addUsers()}
            </AppButton>
          </>
        )}
      </AppPaper>
      <AddUsersModal
        open={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onConfirm={handleAddUsers}
        selectedUsers={selectedUsersList}
      />
      <ProjectNotificationPrompt
        open={showNotificationPrompt}
        onClose={() => setShowNotificationPrompt(false)}
        onConfirm={handleConfirmAddUsers}
        isSubmitting={false}
        description={projectBuilderTranslation.notifyUsersDescription()}
        yesText={projectBuilderTranslation.notifyUsersYes()}
        noText={projectBuilderTranslation.notifyUsersNo()}
      />
    </AppBox>
  )
})

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
    width: '100%',
  },
  projectCard: {
    borderRadius: '12px',
    padding: '10px 15px',
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
    border: 0,
  },
  cardHeader: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: 10,
    width: '100%',
  },
  projectName: {
    fontWeight: 600,
    flex: 1,
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flexShrink: 0,
  },
  dateChip: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: '12px',
    padding: '5px 10px',
    background: ColorTokens.primary100,
  },
  dateChipLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: 'text.secondary',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
  },
  dateChipValue: {
    fontSize: 11,
    fontWeight: 700,
    color: 'text.primary',
    textTransform: 'uppercase' as const,
  },
  menuButton: {
    color: 'text.secondary',
  },
  popoverPaper: {
    borderRadius: '10px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
    minWidth: 280,
  },
  popoverContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    padding: '10px 15px',
  },
  popoverDateRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
  },
  popoverDateLabel: {
    fontSize: 13,
    color: 'text.secondary',
  },
  projectDescription: {
    marginTop: '-15px',
    color: 'text.secondary',
    whiteSpace: 'pre-wrap',
  },
  tagsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
  },
  tag: {
    fontSize: 12,
    padding: '10px 15px',
    borderRadius: '6px',
    backgroundColor: 'grey.100',
    display: 'flex',
    flexDirection: 'column',
  },
  usersSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    marginTop: 4,
    width: '100%',
  },
  usersSectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  sectionIcon: {
    fontSize: 24,
    color: Palette.CORAL_REEF_GREEN,
  },
  usersSectionTitle: {
    fontSize: 14,
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase' as const,
    color: 'text.secondary',
  },
  usersList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    width: '100%',
    alignItems: 'stretch',
  },
  userCard: {
    gap: 15,
    padding: '10px 15px',
    borderRadius: '12px',
    display: 'flex',
    flexDirection: {
      xs: 'column',
      md: 'row',
    },
    alignItems: {
      xs: 'flex-start',
      md: 'center',
    },
    backgroundColor: 'background.dark',
    minHeight: '64px',
    width: '100%',
    minWidth: 0,
    border: '1px solid',
    borderColor: 'transparent',
    cursor: 'pointer',
    transition: 'border-color 0.2s ease-in-out',
    '&:hover': {
      borderColor: ColorTokens.border100,
    },
  },
  avatarWrapper: {
    width: 50,
    height: 50,
    flexShrink: 0,
    marginLeft: '-15px',
  },
  userInfo: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  userName: {
    fontSize: 13,
    fontWeight: 600,
    color: 'text.primary',
  },
  userRoles: {
    fontSize: 12,
    color: Palette.CORAL_REEF_GREEN,
  },
  userSkill: {
    fontSize: 12,
    color: 'text.secondary',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  recommendedCard: {
    borderRadius: '12px',
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
    padding: '10px 15px',
    border: 0,
  },
  emptyText: {
    color: 'text.secondary',
    fontSize: 12,
  },
} as const